import React, { useState, useEffect } from 'react';
import { PlusIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const Tooltip = ({ children, content }: { children: React.ReactNode, content: string }) => (
  <div className="relative flex items-center group/tooltip">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/tooltip:block z-50 whitespace-nowrap bg-slate-800 text-white text-[11px] font-medium px-2.5 py-1.5 rounded shadow-lg pointer-events-none opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200">
      {content}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
    </div>
  </div>
);

// Skeleton Row Component
const LoadingSkeleton = () => {
  return (
    <>
      {[...Array(6)].map((_, i) => (
        <tr key={i} className="animate-pulse border-b border-slate-100">
          <td className="px-4 py-3 border-r border-slate-200">
            <div className="flex items-center gap-3">
              <div className="h-4 w-4 bg-slate-200 rounded"></div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded bg-slate-200"></div>
                <div>
                  <div className="h-4 w-32 bg-slate-200 rounded mb-1.5"></div>
                  <div className="h-3 w-24 bg-slate-200 rounded"></div>
                </div>
              </div>
            </div>
          </td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-32 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-28 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-6 w-24 bg-slate-200 rounded-md"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-20 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3 border-r border-slate-200"><div className="h-4 w-12 bg-slate-200 rounded"></div></td>
          <td className="px-4 py-3"><div className="h-6 w-16 bg-slate-200 rounded"></div></td>
        </tr>
      ))}
    </>
  );
};

const mockCompaniesData = [
  { id: 1, name: 'Acme Corp', website: 'acme.com', industry: 'Software Development', location: 'San Francisco, CA', employees: '1,000 - 5,000', revenue: '$100M+', founded: '2010' },
  { id: 2, name: 'Stark Industries', website: 'stark.io', industry: 'Defense & Space', location: 'New York, NY', employees: '10,000+', revenue: '$1B+', founded: '1974' },
  { id: 3, name: 'Globex', website: 'globex.co', industry: 'Manufacturing', location: 'Austin, TX', employees: '500 - 1,000', revenue: '$50M - $100M', founded: '1995' },
  { id: 4, name: 'Soylent Corp', website: 'soylent.dev', industry: 'Food & Beverage', location: 'Chicago, IL', employees: '200 - 500', revenue: '$10M - $50M', founded: '2018' },
  { id: 5, name: 'Initech', website: 'initech.net', industry: 'IT Services', location: 'Seattle, WA', employees: '5,000 - 10,000', revenue: '$500M+', founded: '1990' },
  { id: 6, name: 'Umbrella Corp', website: 'umbrella.org', industry: 'Biotechnology', location: 'Boston, MA', employees: '10,000+', revenue: '$1B+', founded: '1968' },
];

export default function CompanyTable() {
  const [selectedAll, setSelectedAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<typeof mockCompaniesData>([]);

  // Simulate API fetch delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setCompanies(mockCompaniesData);
      setLoading(false);
    }, 3000); // 3 seconds delay

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col w-full h-fit overflow-hidden">
      
      {/* Table Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
            checked={selectedAll}
            onChange={() => setSelectedAll(!selectedAll)}
            disabled={loading}
          />
          <span className="text-sm font-medium text-slate-700">0 selected <span className="text-slate-400 ml-1">▼</span></span>
        </div>
        
        <div className="flex items-center gap-4 text-sm font-medium text-slate-600">
          <button className="flex items-center gap-1.5 hover:text-brand-700 transition-colors" disabled={loading}>
            <PlusIcon className="w-4 h-4" /> Save Companies
          </button>
          <button className="flex items-center gap-1.5 hover:text-brand-700 transition-colors" disabled={loading}>
            <ArrowDownTrayIcon className="w-4 h-4" /> Export
          </button>
          <button className="flex items-center gap-1.5 text-slate-900 font-semibold transition-colors" disabled={loading}>
            <MagnifyingGlassIcon className="w-4 h-4" /> Saved Searches
          </button>
        </div>
      </div>

      {/* Scrollable Table */}
      <div className="overflow-x-auto bg-white">
        <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 border-b border-slate-200 uppercase tracking-wider">
              <th className="sticky left-0 top-0 z-20 bg-slate-50 px-4 py-3 font-semibold border-r border-slate-200 min-w-[280px]">
                <div className="flex items-center gap-3">
                  <span className="w-4"></span>
                  Company Name
                </div>
              </th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Industry</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Location</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Employees</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Estimated Revenue</th>
              <th className="px-4 py-3 font-semibold border-r border-slate-200">Founded</th>
              <th className="px-4 py-3 font-semibold">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
            {loading ? (
              <LoadingSkeleton />
            ) : (
              companies.map((company) => (
                <tr key={company.id} className="hover:bg-brand-50 transition-colors duration-150 group cursor-pointer">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-brand-50 px-4 py-3 border-r border-slate-200 transition-colors duration-150">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer" />
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-500 group-hover:bg-white group-hover:border-brand-200 transition-colors">
                          {company.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 flex items-center gap-2">
                             {company.name}
                          </div>
                          <div className="text-xs text-brand-600 flex items-center gap-1 hover:underline mt-0.5">
                             <GlobeAltIcon className="w-3 h-3" /> {company.website}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200">{company.industry}</td>
                  <td className="px-4 py-3 border-r border-slate-200">{company.location}</td>
                  <td className="px-4 py-3 border-r border-slate-200">
                    <span className="px-2.5 py-1 rounded bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200 group-hover:bg-white transition-colors">
                      {company.employees}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-r border-slate-200 font-medium text-slate-600">{company.revenue}</td>
                  <td className="px-4 py-3 border-r border-slate-200">{company.founded}</td>
                  <td className="px-4 py-3">
                    <button className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-brand-700 border border-transparent hover:border-brand-200 hover:bg-white px-2 py-1 rounded transition-all">
                      <PlusIcon className="w-3 h-3" /> Save
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white text-sm text-slate-500 shrink-0">
        <span>1 - {loading ? 0 : companies.length} of {loading ? 0 : 45201}</span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <button className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 transition-colors" disabled={loading}>&lt;</button>
            <button className="w-6 h-6 flex items-center justify-center rounded bg-brand-50 text-brand-700 font-medium" disabled={loading}>1</button>
            <button className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 transition-colors" disabled={loading}>&gt;</button>
          </div>
          <span>Go to page <input type="text" className="w-10 border border-slate-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none rounded px-1.5 py-0.5 text-center text-slate-900 transition-all" defaultValue="1" disabled={loading} /></span>
        </div>
      </div>
    </div>
  );
}